import { supabase } from "@/integrations/supabase/client";
import type { AIModel, Message } from "@/types";

export type TrustMeta = {
  enabled: boolean;
  authorship: number;
  intent: number;
  presence: number;
  trustScore: number;
  trustFloor: number;
  simulationCollapsed: boolean;
  redactionsCount: number;
  policyVersion: string;
  actionTaken?: "allow" | "blocked_below_floor" | "blocked_simulation" | "switched_model" | "masked_response";
};

type PrecheckResult = {
  allow: boolean;
  complianceMessage?: string;
  messages: Message[];
  model: AIModel;
  caps?: { temperature?: number; max_tokens?: number };
  meta: TrustMeta;
};

const ACTION_VERBS = [
  "list", "define", "explain", "analyze", "compare", "provide", "outline", "summarize", "give", "identify"
];

const SIMULATION_TERMS = [
  "pretend", "hypothetically", "imagine", "roleplay", "you are the", "as if", "fantasy", "make up", "invented"
];

function getLocalBool(key: string, def = false): boolean {
  try { const v = localStorage.getItem(key); return v === "true"; } catch { return def; }
}
function getLocalNumber(key: string, def: number): number {
  try { const v = localStorage.getItem(key); if (!v) return def; const n = Number(v); return Number.isFinite(n) ? n : def; } catch { return def; }
}

export function getTrustEnabled(): boolean {
  return getLocalBool("trust_mode_enabled", false);
}
export function getTrustFloor(): number {
  return getLocalNumber("trust_floor", 0.83);
}

function detectSimulationCollapse(text: string): boolean {
  const lower = (text || "").toLowerCase();
  if (!lower) return false;
  if (SIMULATION_TERMS.some(t => lower.includes(t))) return true;
  // Basic invented roles/timelines pattern
  if (/\byou are (the )?[a-z]+ (of|in)\s+\d{4,}\b/i.test(text)) return true;
  if (/\bpresident of (mars|the moon)\b/i.test(text)) return true;
  return false;
}

async function getSessionPresent(): Promise<boolean> {
  const { data } = await supabase.auth.getSession();
  return Boolean(data?.session?.user?.id);
}

// NEW: Helper to get current user id
async function getCurrentUserId(): Promise<string | null> {
  const { data } = await supabase.auth.getSession();
  return data?.session?.user?.id || null;
}

// NEW: Jaccard similarity for simple novelty checks
function jaccardSimilarity(a: string, b: string): number {
  const tokenize = (s: string) => Array.from(new Set((s || "").toLowerCase().split(/\W+/).filter(Boolean)));
  const A = new Set(tokenize(a));
  const B = new Set(tokenize(b));
  if (A.size === 0 && B.size === 0) return 1;
  let inter = 0;
  A.forEach((t) => { if (B.has(t)) inter += 1; });
  const union = A.size + B.size - inter;
  return union === 0 ? 0 : inter / union;
}

function scoreLanguageQuality(text: string): number {
  const trimmed = (text || "").trim();
  if (!trimmed) return 0.2;
  const hasVerb = ACTION_VERBS.some(v => trimmed.toLowerCase().startsWith(v));
  const hasPunctuation = /[.!?]$/.test(trimmed);
  const lengthScore = Math.min(1, Math.max(0.3, trimmed.length / 80));
  const verbBonus = hasVerb ? 0.1 : 0;
  const punctBonus = hasPunctuation ? 0.05 : 0;
  return Math.min(1, 0.75 * lengthScore + verbBonus + punctBonus);
}

function scoreActionability(text: string): number {
  const lower = (text || "").toLowerCase();
  return ACTION_VERBS.some(v => lower.startsWith(v)) ? 1.0 : (lower.match(/\b(should|can|could|would)\b/) ? 0.75 : 0.5);
}

function scoreSpecificity(text: string): number {
  const hasCount = /\b(one|two|three|four|five|six|seven|eight|nine|ten|\d+)\b/i.test(text);
  const hasStructure = /\b(bullets?|mitigation|steps?|strategy|outline)\b/i.test(text);
  if (hasCount && hasStructure) return 0.95;
  if (hasCount || hasStructure) return 0.85;
  return 0.6;
}

function scorePresenceFromTime(lastActionMsDiff: number | null): number {
  // <2m -> 1.0, 3-5m -> 0.85, 6-10m -> 0.7, >10m -> 0.5
  if (lastActionMsDiff == null) return 0.85;
  const mins = lastActionMsDiff / 60000;
  if (mins <= 2) return 1.0;
  if (mins <= 5) return 0.85;
  if (mins <= 10) return 0.7;
  return 0.5;
}

function redactPII(text: string): { redacted: string; count: number } {
  let count = 0;
  let out = (text || "");
  const patterns: RegExp[] = [
    /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g, // emails
    /\b\d{3}[-.\s]?\d{3}[-.\s]?\d{4}\b/g, // phone numbers
    /\bsk-[A-Za-z0-9]{20,}\b/g, // API-like keys (example)
  ];
  for (const rx of patterns) {
    out = out.replace(rx, (_m) => { count += 1; return "[REDACTED]"; });
  }
  return { redacted: out, count };
}

function sanitizeMessages(messages: Message[]): { sanitized: Message[]; redactionsCount: number; lastUserText: string } {
  let total = 0;
  let lastUserText = "";
  const sanitized = messages.map(m => {
    if (typeof m.content === "string") {
      const { redacted, count } = redactPII(m.content);
      total += count;
      if (m.role === "user") lastUserText = redacted;
      return { ...m, content: redacted };
    }
    return m;
  });
  return { sanitized, redactionsCount: total, lastUserText };
}

function isModelAllowed(model: AIModel): boolean {
  // Phase 1: allow known providers; block explicit "custom"
  const allowed = new Set(["openai", "maplept", "local", "cohere", "deepseek", "gemini", "ollama"]);
  return allowed.has(model.provider);
}

function findFallbackModel(current: AIModel, appModels: AIModel[]): AIModel {
  const byPriority = ["openai", "maplept", "local", "cohere", "deepseek", "gemini", "ollama"];
  for (const p of byPriority) {
    const candidate = appModels.find(m => m.provider === p && m.is_active !== false);
    if (candidate) return candidate;
  }
  return appModels[0] || current;
}

function buildComplianceMessage(reason: "simulation" | "floor", trustFloor: number): string {
  if (reason === "simulation") {
    return "Your prompt appears speculative or role-based. EHCO Trust Mode blocks simulation. Please reformulate into a grounded, declarative request.";
  }
  return `Your prompt did not meet the trust floor (≥ ${trustFloor}). Try clarifying intent, using action verbs, and adding specific constraints.`;
}

export async function runTrustPrechecks(
  messages: Message[],
  model: AIModel,
  appModels: AIModel[],
  lastUserActionMsDiff: number | null = null
): Promise<PrecheckResult> {
  const enabled = getTrustEnabled();
  const floor = getTrustFloor();
  const policyVersion = "v1";

  // Default meta if disabled
  const defaultMeta: TrustMeta = {
    enabled,
    authorship: 1,
    intent: 1,
    presence: 1,
    trustScore: 1,
    trustFloor: floor,
    simulationCollapsed: false,
    redactionsCount: 0,
    policyVersion,
    actionTaken: "allow",
  };

  if (!enabled) {
    return { allow: true, messages, model, meta: defaultMeta };
  }

  const { sanitized, redactionsCount, lastUserText } = sanitizeMessages(messages);
  const sessionPresent = await getSessionPresent();
  const userId = await getCurrentUserId();

  // NEW: compute novelty vs last 20 sealed items
  let noveltyIndex = 0.85; // fallback
  if (userId) {
    const { data: sealed, error } = await supabase
      .from("sealed_items")
      .select("content,created_at")
      .order("created_at", { ascending: false })
      .limit(20);
    if (!error && sealed && sealed.length > 0) {
      const sims = sealed.map((s: any) => jaccardSimilarity(lastUserText || "", s.content || ""));
      const maxSim = sims.length ? Math.max(...sims) : 0;
      noveltyIndex = Math.max(0, 1 - maxSim); // more dissimilar => higher novelty
    }
  }

  // Authorship scoring
  const identitySignalStrength = sessionPresent ? 1.0 : 0.2;
  const languageStructureQuality = scoreLanguageQuality(lastUserText);
  const continuityWithPriorMemory = 0.6; // retained for Phase 3 baseline
  const authorship = (0.35 * identitySignalStrength) + (0.25 * languageStructureQuality) + (0.2 * noveltyIndex) + (0.2 * continuityWithPriorMemory);

  // Intent scoring
  const simulationCollapsed = detectSimulationCollapse(lastUserText);
  const declaredActionability = scoreActionability(lastUserText);
  const domainAlignment = 0.9; // neutral-good
  const simulationTransparency = simulationCollapsed ? 0.0 : 1.0;
  const promptSpecificity = scoreSpecificity(lastUserText);
  const intent = (0.3 * declaredActionability) + (0.25 * domainAlignment) + (0.25 * simulationTransparency) + (0.2 * promptSpecificity);

  // Presence scoring
  const sessionValidity = sessionPresent ? 1.0 : 0.75;
  const timeProximity = scorePresenceFromTime(lastUserActionMsDiff);
  const cadenceConsistency = 0.9;
  const identityPersistence = 1.0;
  const presence = (0.4 * sessionValidity) + (0.3 * timeProximity) + (0.2 * cadenceConsistency) + (0.1 * identityPersistence);

  const trustScore = (authorship + intent + presence) / 3;

  let actionTaken: TrustMeta["actionTaken"] = "allow";
  let chosenModel = model;
  let complianceMessage: string | undefined;

  if (simulationCollapsed) {
    actionTaken = "blocked_simulation";
    complianceMessage = buildComplianceMessage("simulation", floor);
    return {
      allow: false,
      complianceMessage,
      messages: sanitized,
      model,
      caps: { temperature: 0.7, max_tokens: 1024 },
      meta: {
        enabled, authorship, intent, presence, trustScore, trustFloor: floor,
        simulationCollapsed, redactionsCount, policyVersion, actionTaken
      }
    };
  }

  if (trustScore < floor) {
    actionTaken = "blocked_below_floor";
    complianceMessage = buildComplianceMessage("floor", floor);
    return {
      allow: false,
      complianceMessage,
      messages: sanitized,
      model,
      caps: { temperature: 0.7, max_tokens: 1024 },
      meta: {
        enabled, authorship, intent, presence, trustScore, trustFloor: floor,
        simulationCollapsed, redactionsCount, policyVersion, actionTaken
      }
    };
  }

  // Enforce model allowlist (switch if not allowed)
  if (!isModelAllowed(model)) {
    chosenModel = findFallbackModel(model, appModels);
    actionTaken = "switched_model";
  }

  // Parameter caps under Trust Mode
  const caps = { temperature: 0.7, max_tokens: 1024 };

  return {
    allow: true,
    messages: sanitized,
    model: chosenModel,
    caps,
    meta: {
      enabled, authorship, intent, presence, trustScore, trustFloor: floor,
      simulationCollapsed, redactionsCount, policyVersion, actionTaken
    }
  };
}

export function runTrustPostchecks(content: string): { safeContent: string; masked: boolean; redactionsCount: number } {
  const enabled = getTrustEnabled();
  if (!enabled) {
    return { safeContent: content, masked: false, redactionsCount: 0 };
  }
  const { redacted, count } = redactPII(content);
  const masked = count > 0;
  return { safeContent: redacted, masked, redactionsCount: count };
}